import { ExportFormat } from '@ecosystem/newron-client-lib';
import { Dispatch, SetStateAction } from 'react';
import { BaseChartResp, ValueChartResp, VolumeChartResp } from '@data-visualization/share-comparison-chart/Types';
import { RetailerKpiOption, SaleType, TrendsKpi } from '@models/Products';
import { Kpi } from '@utils/Formatters';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { OnExportReadyCallback } from '@ecosystem/export-utils';
import { FactRevenue, PageName } from '@utils/pageConfig/Types';
import { RetailerClientStateKey } from '@utils/page-analytics/model/ChartInteractionTracking';

export class ProductsLineChartResp {
  value = new ValueChartResp();
  volume = new VolumeChartResp();
  price: PriceChartResp = new PriceChartResp();
}

export class PriceChartResp {
  marketPrice: BaseChartResp[] = [];
  retailerPrice: BaseChartResp[] = [];
  retailerPriceShare: BaseChartResp[] = [];
}

export const SaleTypeMarketKpi = {
  [SaleType.revenue]: Kpi.marketRevenue,
  [SaleType.volume]: Kpi.marketVolume,
  [SaleType.price]: Kpi.marketPrice,
};

export const SaleTypeMarketResp = {
  [SaleType.revenue]: (chartResp: ProductsLineChartResp) => chartResp.value.marketValue,
  [SaleType.volume]: (chartResp: ProductsLineChartResp) => chartResp.volume.marketVolume,
  [SaleType.price]: (chartResp: ProductsLineChartResp) => chartResp.price.marketPrice,
};

export const SaleTypeRetailerKpi = {
  [SaleType.revenue]: Kpi.retailerRevenue,
  [SaleType.volume]: Kpi.retailerVolume,
  [SaleType.price]: Kpi.retailerPrice,
};

export const SaleTypeRetailerShareKpi = {
  [SaleType.revenue]: Kpi.retailerShareRevenue,
  [SaleType.volume]: Kpi.retailerShareVolume,
  [SaleType.price]: Kpi.retailerPrice,
};

export const SaleTypeRetailerResp = {
  [SaleType.revenue]: (chartResp: ProductsLineChartResp) => chartResp.value.retailerValue,
  [SaleType.volume]: (chartResp: ProductsLineChartResp) => chartResp.volume.retailerVolume,
  [SaleType.price]: (_: unknown) => undefined,
};

export const SaleTypeRetailerShareResp = {
  [SaleType.revenue]: (option: RetailerKpiOption) => getSaleTypeRetailerResponseTypeRevenue[option],
  [SaleType.volume]: (option: RetailerKpiOption) => getSaleTypeRetailerResponseTypeVolume[option],
  [SaleType.price]: (_: unknown) => (chartResp: ProductsLineChartResp) => chartResp.price.retailerPrice,
};

export const getSaleTypeRetailerResponseTypeRevenue = {
  [RetailerKpiOption.mix]: (chartResp: ProductsLineChartResp) => chartResp.value.retailerValueMixShare,
  [RetailerKpiOption.share]: (chartResp: ProductsLineChartResp) => chartResp.value.retailerValueShare,
  [RetailerKpiOption.absolute]: (chartResp: ProductsLineChartResp) => chartResp.value.retailerValue,
};

export const getSaleTypeRetailerResponseTypeVolume = {
  [RetailerKpiOption.mix]: (chartResp: ProductsLineChartResp) => chartResp.volume.retailerVolumeMixShare,
  [RetailerKpiOption.share]: (chartResp: ProductsLineChartResp) => chartResp.volume.retailerVolumeShare,
  [RetailerKpiOption.absolute]: (chartResp: ProductsLineChartResp) => chartResp.volume.retailerVolume,
};

export interface CommonCardProps {
  trendsKpi: TrendsKpi;
  selectedPeriodicity: Periodicity;
  selectedLineChartSaleType: SaleType;
  setSelectedLineChartSaleType?: Dispatch<SetStateAction<SaleType>>;
  factRevenue: FactRevenue;
  isLoading: boolean;
  excludeTooltipIcon?: boolean;
  onExportReady?: OnExportReadyCallback;
  pageTabName?: PageName; //Used to differentiate tab name in Products Page, currently for export chart sizing, additional child components in the future.
}

export interface MarketCardStateProp {
  excludeFilter?: boolean;
  isPeriodYTDorMAT?: boolean;
}

export interface RetailerCardStateProp {
  selectedOption: RetailerKpiOption;
}

export interface RetailerCardSetStateProp {
  selectedRetailerOption?: RetailerKpiOption;
  setSelectedOption?: Dispatch<SetStateAction<RetailerKpiOption>>;
  clickHandler?: (value: string) => void;
  getCompareProductsHeaderIcon?: Function;
  retailerClientStateKey?: RetailerClientStateKey;
}

export interface RetailerCardDisabledProp {
  disabled: boolean;
  excludeFilter?: boolean;
}

export type RetailerCardProps = RetailerCardStateProp & RetailerCardSetStateProp;

export interface RetailerLineChartTooltipOptions {
  data: TrendsKpi;
  secondKpi: Kpi;
  getRetailerBaseChartResp: (rollingSalesDto: ProductsLineChartResp) => BaseChartResp[] | undefined;
}

export interface ExportExcelProps {
  isExportButtonDisabled: boolean;
  onChartDownload: (exportFormats: ExportFormat[]) => Promise<void>;
}
